import React, { useEffect, useState } from "react";
import api, { fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { Share2, Copy, Twitter, Link as LinkIcon, X, EyeOff, Eye, Gift } from "lucide-react";
import { toast } from "sonner";

export default function ShareTaxSavingsModal({ open, onClose, taxSaved }) {
  const [shareState, setShareState] = useState(null);
  const [loading, setLoading] = useState(false);

  const baseUrl = window.location.origin;
  // OG-wrapped URL that crawlers can unfurl. Humans land on /share/:slug via redirect.
  const ogUrl = shareState?.slug ? `${baseUrl}/api/share/og/${shareState.slug}` : "";
  const directUrl = shareState?.slug ? `${baseUrl}/share/${shareState.slug}` : "";

  useEffect(() => {
    if (!open) return;
    api.get("/share/me").then((r) => setShareState(r.data)).catch(() => toast.error("Could not load share status"));
  }, [open]);

  const enable = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/share/create");
      setShareState(data);
      toast.success("Public link enabled");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    setLoading(true);
    try {
      await api.delete("/share/revoke");
      setShareState({ ...shareState, enabled: false });
      toast.success("Public link disabled");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const togglePrivacy = async (hide) => {
    // optimistic
    setShareState((s) => ({ ...s, hide_business_name: hide }));
    try {
      const { data } = await api.patch("/share/settings", { hide_business_name: hide });
      setShareState(data);
    } catch (e) {
      // revert
      setShareState((s) => ({ ...s, hide_business_name: !hide }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(ogUrl);
    toast.success("Link copied (unfurls with preview)");
  };

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `I just kept ${fmtMoney(taxSaved)} in business taxes this year using BizCashflow Pros. Sign up via my link and we both get a free month:`
  )}&url=${encodeURIComponent(ogUrl)}`;

  if (!open) return null;

  const earned = shareState?.free_months_earned ?? 0;
  const balance = shareState?.free_months_balance ?? 0;
  const redeemed = shareState?.free_months_redeemed ?? 0;
  const cap = shareState?.free_months_cap ?? 12;
  const capReached = !!shareState?.cap_reached;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm" data-testid="share-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-2 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-forest/8 text-forest flex items-center justify-center">
              <Share2 className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-heading text-xl font-medium">Share your tax win</h3>
              <p className="text-xs text-ink-lighter">Each signup = 1 free month (up to {cap}).</p>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-lighter hover:text-ink p-1" data-testid="share-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          <div className="card-surface p-5 mb-5 bg-paper">
            <div className="overline mb-1.5">Public card will show</div>
            <div className="num font-heading text-3xl font-medium text-forest mb-1">{fmtMoney(taxSaved)}</div>
            <div className="text-xs text-ink-lighter">in taxes saved this year · your first name{shareState?.hide_business_name ? "" : " and business name"} · no transactions</div>
          </div>

          {!shareState ? (
            <div className="text-sm text-ink-lighter">Loading…</div>
          ) : shareState.enabled && shareState.slug ? (
            <>
              <div className="flex items-center gap-2 mb-2" data-testid="share-link-row">
                <div className="flex-1 bg-paper border border-border rounded-lg px-3 py-2 text-sm font-mono text-ink-light truncate" data-testid="share-link">
                  {directUrl}
                </div>
                <button onClick={copy} className="btn-secondary inline-flex items-center gap-1.5" data-testid="share-copy">
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <div className="text-[11px] text-ink-lighter mb-4">
                Copy shares the unfurl-friendly link so Twitter / Slack / iMessage show a preview card.
              </div>

              <div className="grid grid-cols-2 gap-2 mb-5">
                <a href={tweetUrl} target="_blank" rel="noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="share-twitter">
                  <Twitter className="w-4 h-4" /> Tweet
                </a>
                <a href={directUrl} target="_blank" rel="noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="share-preview">
                  <LinkIcon className="w-4 h-4" /> Preview
                </a>
              </div>

              <div className="card-surface bg-forest/5 border-forest/15 p-4 mb-3" data-testid="referral-stats">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-forest text-white flex items-center justify-center shrink-0">
                    <Gift className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-ink mb-0.5">Free month balance</div>
                    <div className="text-xs text-ink-light">
                      {balance > 0
                        ? `${balance} free month${balance > 1 ? "s" : ""} available to redeem.`
                        : "Refer a friend to earn your first free month."}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="num text-2xl font-heading font-medium text-forest leading-none" data-testid="balance-value">{balance}</div>
                    <div className="text-[10px] text-ink-lighter mt-1">available</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-3 border-t border-forest/15 text-center">
                  <div>
                    <div className="num text-base font-medium" data-testid="referral-count">{shareState.referral_count}</div>
                    <div className="text-[10px] text-ink-lighter uppercase tracking-wider mt-0.5">Referrals</div>
                  </div>
                  <div>
                    <div className="num text-base font-medium text-forest" data-testid="earned-value">{earned}<span className="text-ink-lighter text-xs">/{cap}</span></div>
                    <div className="text-[10px] text-ink-lighter uppercase tracking-wider mt-0.5">Earned</div>
                  </div>
                  <div>
                    <div className="num text-base font-medium" data-testid="redeemed-value">{redeemed}</div>
                    <div className="text-[10px] text-ink-lighter uppercase tracking-wider mt-0.5">Redeemed</div>
                  </div>
                </div>
                {capReached && (
                  <div className="mt-3 text-[11px] text-ochre bg-ochre/10 rounded-md px-3 py-2 text-center">
                    You've hit the {cap}-month cap. Keep sharing — the credit still helps your friends discover deductions.
                  </div>
                )}
              </div>

              <label className="flex items-center justify-between text-sm py-3 border-t border-border cursor-pointer" data-testid="privacy-toggle-label">
                <div>
                  <div className="font-medium text-ink">Hide business name</div>
                  <div className="text-xs text-ink-lighter mt-0.5">Show only your first name on the public card</div>
                </div>
                <button
                  type="button"
                  onClick={() => togglePrivacy(!shareState.hide_business_name)}
                  data-testid="privacy-toggle"
                  aria-pressed={!!shareState.hide_business_name}
                  className={`relative w-10 h-5 rounded-full transition-colors ${shareState.hide_business_name ? "bg-forest" : "bg-ink-lighter/40"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${shareState.hide_business_name ? "translate-x-5" : ""}`} />
                </button>
              </label>

              <div className="flex items-center justify-between text-xs text-ink-lighter pt-4 border-t border-border">
                <span data-testid="share-view-count">{(shareState.view_count || 0).toLocaleString()} views so far</span>
                <button onClick={revoke} disabled={loading} className="inline-flex items-center gap-1.5 hover:text-destructive" data-testid="share-revoke">
                  <EyeOff className="w-3.5 h-3.5" /> Disable link
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="card-surface bg-forest/5 border-forest/15 p-4 mb-5">
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-forest mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div className="text-sm">
                    <div className="font-medium text-ink mb-1">Refer-a-friend, get a free month</div>
                    <div className="text-xs text-ink-light leading-relaxed">
                      Anyone who signs up through your link gets onboarded — you earn one free month per signup, up to {cap}.
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-ink-light mb-5 leading-relaxed">
                Only your <span className="font-medium text-ink">first name</span>,
                business name (optional), and aggregate numbers are shown — never raw transactions.
              </p>
              <button onClick={enable} disabled={loading} className="btn-forest w-full inline-flex items-center justify-center gap-2" data-testid="share-enable">
                <Eye className="w-4 h-4" /> {loading ? "Enabling…" : "Enable public link"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
